import React, { useEffect, useMemo, useState } from 'react'
import { Button, Modal, Stack, Text } from '@mantine/core'
import { useSelector } from 'react-redux'
import { filterCurrentOrgExperiences } from '../hooks/useWorkspaceContext'

const SwitchExperienceModal = ({ show, onClose, onSelect }) => {
	const [showPastExperiences, setShowPastExperiences] = useState(false)
	const currentExpId = useSelector((state) => state.user.currentExperience?.id)
	const currentOrgId = useSelector((state) => state.user.currentOrg?.orgId)
	const currentOrgSlug = useSelector((state) => state.user.currentOrg?.orgSlug)
	const experiences = useSelector((state) => state.org.userPositions || [])

	useEffect(() => {
		if (!show) setShowPastExperiences(false)
	}, [show])

	const currentOrgExperiences = useMemo(() => {
		return filterCurrentOrgExperiences(experiences, {
			orgId: currentOrgId,
			orgSlug: currentOrgSlug,
		})
	}, [currentOrgId, currentOrgSlug, experiences])

	const activeExperiences = currentOrgExperiences.filter(
		(exp) => exp.experienceStatus !== 'inactive',
	)
	const pastExperiences = currentOrgExperiences.filter(
		(exp) => exp.experienceStatus === 'inactive',
	)
	const showPastButton = !showPastExperiences && pastExperiences.length > 0
	const hasVisibleExperiences =
		activeExperiences.length > 0 ||
		showPastButton ||
		(showPastExperiences && pastExperiences.length > 0)

	const renderExperienceButton = (exp) => {
		const isCurrentExperience = exp.experienceId === currentExpId

		return (
			<Button
				key={exp.experienceId}
				variant={isCurrentExperience ? 'filled' : 'light'}
				color={isCurrentExperience ? 'gray' : 'blue'}
				disabled={isCurrentExperience}
				fullWidth
				onClick={() => onSelect(exp)}
			>
				<Text truncate size='sm' fw={500}>
					{exp.experienceName}
				</Text>
			</Button>
		)
	}

	return (
		<Modal
			opened={show}
			onClose={onClose}
			title='Switch Experience'
			centered
			overlayProps={{ opacity: 0.4, blur: 2 }}
			size='sm'
		>
			<Stack>
				{activeExperiences.map(renderExperienceButton)}

				{showPastButton && (
					<Button
						variant='light'
						color='gray'
						size='xs'
						fullWidth
						radius='sm'
						onClick={() => setShowPastExperiences(true)}
						styles={(theme) => ({
							root: {
								height: 32,
								fontWeight: 600,
								backgroundColor:
									theme.colorScheme === 'dark'
										? theme.colors.dark[5]
										: theme.colors.gray[0],
								color:
									theme.colorScheme === 'dark'
										? theme.colors.gray[2]
										: theme.colors.gray[7],
							},
						})}
					>
						Show Inactive Experiences
					</Button>
				)}

				{showPastExperiences && pastExperiences.length > 0 && (
					<Stack
						spacing='xs'
						pt='xs'
						sx={(theme) => ({
							borderTop: `1px solid ${
								theme.colorScheme === 'dark'
									? theme.colors.dark[4]
									: theme.colors.gray[3]
							}`,
						})}
					>
						<Text
							c='dimmed'
							size='xs'
							fw={700}
							style={{ textTransform: 'uppercase' }}
						>
							Past experiences
						</Text>
						{pastExperiences.map(renderExperienceButton)}
					</Stack>
				)}

				{!hasVisibleExperiences && (
					<Text c='dimmed' ta='center' size='sm'>
						No experiences are available.
					</Text>
				)}
			</Stack>
		</Modal>
	)
}

export default SwitchExperienceModal
